<script setup lang="ts">
import { computed } from 'vue'

import { JustWatchOfferMonetizationType } from '@xcel-next/core/enums'

import { TabContainer, Tab } from '../shared/tabs'
import { useCdnImages } from '../../composables/use-cdn-images'

import type { VodStreamingOptionsParagraph } from '../../utils/paragraph-schema/paragraphs'

type StreamingOptions = VodStreamingOptionsParagraph['vodStreamingOptions']
type StreamingOffer = NonNullable<StreamingOptions['free']>[number]

const props = withDefaults(defineProps<{
  vodStreamingOptions: StreamingOptions
  // no heading tag when embedded in a teaser - the offers headline is no relevant page content (SEO)
  avoidHeadlineTag?: boolean
}>(), {
  avoidHeadlineTag: false
})

type MonetizationTab = {
  title: string,
  type: string,
  dataKey: keyof StreamingOptions
}

const TABS: MonetizationTab[] = [
  { title: 'GRATIS', type: 'free', dataKey: JustWatchOfferMonetizationType.free },
  { title: 'ABO', type: 'subscription', dataKey: JustWatchOfferMonetizationType.flatrate },
  { title: 'KAUFEN', type: 'buy', dataKey: JustWatchOfferMonetizationType.buy },
  { title: 'LEIHEN', type: 'rent', dataKey: JustWatchOfferMonetizationType.rent }
]

const { cdnImageUrl } = useCdnImages()

const getTabData = (key: keyof StreamingOptions) => props.vodStreamingOptions[key] ?? []

const filteredTabs = computed(() => TABS.filter(tab => getTabData(tab.dataKey).length > 0))

const getPrice = (offer: StreamingOffer) =>
  offer.retailPrice ? 'ab ' + offer.retailPrice + (offer.currency === 'EUR' ? '€' : ' ' + offer.currency) : ''
</script>

<template>
  <div class="bx-content bx-streaming-offers bx-typo--vod-epg bx-typo--streaming-offers">
    <component :is="avoidHeadlineTag ? 'div' : 'h3'"
               :class="{ 'bx-typo--h3': avoidHeadlineTag }">
      Streaming-Angebote
    </component>
    <TabContainer>
      <Tab v-for="tab in filteredTabs"
           :key="tab.title"
           :title="tab.title">
        <div class="bx-streaming-offers__monetization-type"
             :data-tc="`streaming-offers-${tab.type}`">
          <ul class="bx-streaming-offers__offer">
            <li v-for="offer in getTabData(tab.dataKey)"
                :key="offer.id"
                class="bx-streaming-offers__offer__content"
                :title="offer.provider?.clearName">
              <a :href="offer.urls.standardWeb"
                 target="_blank"
                 rel="noopener">
                <img :src="offer.provider?.iconUrl.replace(/s100$/, 's50') /** we are fine with smaller provider logos */"
                     :alt="offer.provider?.clearName"
                     width="50"
                     height="50"
                     loading="lazy"
                     class="bx-icon--provider-icon">
                <span class="bx-streaming-offers__price">{{ getPrice(offer) }}</span>
              </a>
            </li>
          </ul>
        </div>
      </Tab>
    </TabContainer>
    <span class="bx-streaming-offers__footer">
      <div>
        Beim Kauf über einen der verlinkten Streaming-Anbieter (Affiliate Link)
        erhalten wir eine geringfügige Provision, die redaktionelle Selektion und
        Beschreibung der Angebote wird dadurch nicht beeinflusst.
      </div>
      <div>
        powered by
        <a :href="'https://www.justwatch.com/'"
           target="_blank"
           rel="noopener">
          <img :src="cdnImageUrl('/images/tvmovie/justwatch-logo-yellow.svg')"
               alt="Justwatch Logo"
               width="80"
               height="16"
               loading="lazy"
               class="bx-icon--justwatch-logo">
        </a>
      </div>
    </span>
  </div>
</template>

